// The drafts inbox: the life of a review draft, and what publishing sends.
//
// Nothing is pre-approved. A draft is included in the review being
// prepared, or it is not, and every draft starts not included, whoever
// wrote it (the user, an agent) and wherever it came from (Changeling, or
// the viewer's pending review on GitHub, which is mirrored here):
//
//   create ─────▶ not included ◀──include / take out──▶ included(at, head)
//                     ▲   │ edit (stays)                  │  │ edit (stays)
//                     │   ▼                               │  ▼
//                     └──────the pull request's head moves on (see `shownState`)
//
// The user decides when they send: the Review view lists every draft with
// a box to tick, and sends exactly the ticked ones. Discarding takes a
// draft out either way (Undo brings it back as it was). The server refuses
// one included on another head; `publishPlan` is the one place that
// decides what a publish sends, and both the page and the publish read it.
//
// On the wire "not included" is `toReview` and "included" is `accepted`
// (see api/server `DraftState`); those two words are never shown.
import type { DraftState, PublishRequest, PublishResponse, ReviewDraft } from '../api/server';
import { parseOffDiff } from './offdiff';
import type { Snapshot } from './timeline';

export type Verdict = 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES';

// ---------------------------------------------------------------- transitions

/** The state every new draft starts in: not included. */
export const NOT_INCLUDED: DraftState = { kind: 'toReview' };

/** Including it in the review being prepared (again, if it was): included now, on head `head`. */
export const include = (now: string, head: string): DraftState => ({ kind: 'accepted', at: now, head });

/** Taking it out of the review being prepared. */
export const takeOut = (): DraftState => NOT_INCLUDED;

/** Whether a draft is included in the review being prepared (pass it as shown, see `shownState`). */
export const isIncluded = (d: Pick<ReviewDraft, 'state'>): boolean => d.state.kind === 'accepted';

/** Editing keeps the state: an included draft stays included, one that is not stays out. */
export const edited = (d: ReviewDraft, body: string, now: string): ReviewDraft => ({ ...d, body, updated_at: now });

/** What the user reads for a draft's state. */
export const stateText = (d: Pick<ReviewDraft, 'state'>): string => (isIncluded(d) ? 'included' : 'not included');

// ---------------------------------------------------------------- where it came from

/** The GitHub comment a draft mirrors while that comment is in the viewer's pending review; else null. */
export function mirroredComment(d: Pick<ReviewDraft, 'origin'>): string | null {
  return d.origin.kind === 'pendingReview' && d.origin.on_github ? d.origin.github_id : null;
}

/** Asked before discarding a draft whose comment is in the viewer's pending review on GitHub: the discard reaches GitHub. */
export const MIRRORED_DISCARD =
  'This draft is a comment in your pending review on GitHub. Discarding it deletes that comment on GitHub too, if it is still pending there.\n\nDiscard it?';

/**
 * What a draft's card says of where it came from: nothing for one written
 * in Changeling; for one from the viewer's pending review on GitHub, that
 * (`label`) and, when it applies, how it differs from GitHub now (`note`,
 * with `why` in full).
 */
export function originText(d: Pick<ReviewDraft, 'origin'>): { label: string; note: string | null; why: string | null } | null {
  const o = d.origin;
  if (o.kind !== 'pendingReview') return null;
  const label = 'from your pending review on GitHub';
  if (!o.on_github)
    return {
      label,
      note: 'not on GitHub now, kept here',
      why: 'This comment is not in a pending review on GitHub now. Changeling keeps it as a draft; unless you send or discard it, a publish puts it back in a pending review when GitHub can take it there (a line comment written on another commit than the pending review cannot be put back, and stays here).',
    };
  if (o.edited)
    return {
      label,
      note: 'edited here, not yet on GitHub',
      why: 'You changed its text in Changeling. GitHub still has the text you wrote there until you publish this draft.',
    };
  return { label, note: null, why: null };
}

// ---------------------------------------------------------------- the anchor

/** The commit whose lines a draft names, or null when it names none (a reply; a file-level draft without a line marker). */
export function anchorCommit(d: ReviewDraft): string | null {
  switch (d.target.kind) {
    case 'line':
    case 'conversation':
      return d.target.commit_sha;
    case 'file':
      // A file-level draft for a line outside the hunks names its line in its marker.
      return parseOffDiff(d.body) ? d.target.commit_sha : null;
    case 'reply':
    case 'pr':
      return null;
  }
}

/** The line a draft is shown at (its last line), or null. */
export function anchorLine(d: ReviewDraft): number | null {
  if (d.target.kind === 'file') return parseOffDiff(d.body)?.marker.line ?? null;
  if (d.target.kind === 'pr') return null;
  return d.target.line;
}

/**
 * The state a draft is shown in: an included draft is not included any more
 * once the pull request's head is no longer the one it was included on (new
 * commits were pushed), whatever its kind. The server applies the same rule
 * when publishing. Without a known head nothing changes.
 */
export function shownState(d: ReviewDraft, head: string): DraftState {
  if (d.state.kind !== 'accepted' || !head || d.state.head === head) return d.state;
  return takeOut();
}

/** Drafts with the state they are shown in. */
export function withShownStates(drafts: readonly ReviewDraft[], head: string): ReviewDraft[] {
  return drafts.map((d) => {
    const state = shownState(d, head);
    return state === d.state ? d : { ...d, state };
  });
}

// ---------------------------------------------------------------- the list

/** Drafts by file, files in path order, each file's drafts by line (lineless first), then age. */
export function byFile(drafts: readonly ReviewDraft[]): { path: string; drafts: ReviewDraft[] }[] {
  const files = new Map<string, ReviewDraft[]>();
  for (const d of drafts) files.set(d.target.path, [...(files.get(d.target.path) ?? []), d]);
  return [...files.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([path, list]) => ({
      path,
      drafts: list.sort(
        (a, b) => (anchorLine(a) ?? 0) - (anchorLine(b) ?? 0) || a.created_at.localeCompare(b.created_at) || String(a.id).localeCompare(String(b.id)),
      ),
    }));
}

/** The drafts in the order the list shows them. */
export function listOrder(drafts: readonly ReviewDraft[]): ReviewDraft[] {
  return byFile(drafts).flatMap((f) => f.drafts);
}

// ---------------------------------------------------------------- publishing

/** Draft replies that, published, resolve their thread. */
const RESOLVING_WORDS = new Set(['done', 'ack']);

/**
 * The thread draft `d` answers with Done or Ack, whether or not it is
 * included yet; else null. Done and Ack are not offered again on a thread
 * a draft answers: the answer is written, and waits to be ticked.
 */
export function answeredBy(d: ReviewDraft): string | null {
  return d.target.kind === 'reply' && RESOLVING_WORDS.has(d.body.trim().toLowerCase()) ? d.target.thread_id : null;
}

/**
 * The thread draft `d` resolves once published: an included reply that
 * says Done or Ack (where the viewer may resolve it, see `publishPlan`);
 * else null. A Done that is not ticked is not sent, so it resolves nothing.
 */
export function resolvedBy(d: ReviewDraft): string | null {
  return isIncluded(d) ? answeredBy(d) : null;
}

export interface PublishState {
  /** The drafts, in the state they are shown in (see `withShownStates`). */
  drafts: readonly ReviewDraft[];
  snapshots: readonly Snapshot[];
  /** The pull request's head commit. */
  head: string;
  /** The commit a review covers when no line draft names one: the diff on screen, else the head. */
  fallbackCommit: string;
  verdict: Verdict;
  /** The summary, as typed. */
  body: string;
  /** Whether the viewer may resolve a thread (a Done or Ack reply resolves it once the review is in). */
  canResolve: (threadId: string) => boolean;
}

/** What the Review view's Publish button publishes, exactly. */
export interface PublishPlan {
  /** The review's verdict, or null when no review is submitted (conversation comments only). */
  verdict: Verdict | null;
  /** The review's body as sent: the summary, then the included pull request comments (`reviewBody`). */
  body: string;
  /** The commit the review is made on. */
  commit: string;
  /** Included drafts published in the review, in list order. */
  inline: ReviewDraft[];
  /** Included drafts posted to the conversation (lines outside the diff), in list order. */
  conversation: ReviewDraft[];
  /** Drafts not included: they stay drafts. */
  notIncluded: ReviewDraft[];
  /** Included line drafts on another commit than the review's: a review covers one commit, so they wait for a later one. */
  otherCommit: ReviewDraft[];
  /** Review threads resolved once the review is in. */
  resolve: string[];
}

/**
 * Whether `plan` only repeats an Approve the viewer just sent (`last`, the
 * verdict of the last publish from the same page): Approve again with no
 * summary, no drafts and no thread to resolve. GitHub takes a repeat
 * approval; the Review view refuses one, so a double click never sends one.
 */
export function repeatsApproval(last: Verdict | null, plan: PublishPlan): boolean {
  return (
    last === 'APPROVE' &&
    plan.verdict === 'APPROVE' &&
    !plan.body.trim() &&
    !plan.inline.length &&
    !plan.conversation.length &&
    !plan.resolve.length
  );
}

/**
 * The publish `state` asks for. Only included drafts go. The review is made
 * on the head when an included line draft names it, else on the newest
 * commit they name (else `fallbackCommit`); line drafts on another commit
 * wait. A review is submitted when it has
 * drafts, a verdict other than Comment or a summary, or when nothing goes
 * to the conversation (a plain Comment); otherwise only the conversation
 * comments are posted.
 */
export function publishPlan(state: PublishState): PublishPlan {
  const order = listOrder(state.drafts);
  const accepted = order.filter(isIncluded);
  const rank = (sha: string) => state.snapshots.find((s) => s.sha === sha)?.n ?? 0;
  const lineCommits = accepted.flatMap((d) => (d.target.kind === 'line' ? [d.target.commit_sha] : []));
  const commit = lineCommits.includes(state.head)
    ? state.head
    : (lineCommits.reduce<string | null>((best, c) => (best === null || rank(c) > rank(best) ? c : best), null) ?? state.fallbackCommit);
  const elsewhere = (d: ReviewDraft) => d.target.kind === 'line' && d.target.commit_sha !== commit;
  const inline = accepted.filter((d) => d.target.kind !== 'conversation' && !elsewhere(d));
  const conversation = accepted.filter((d) => d.target.kind === 'conversation');
  const due = inline.length > 0 || state.verdict !== 'COMMENT' || state.body.trim() !== '' || conversation.length === 0;
  const resolve = due
    ? inline.flatMap((d) => {
        const thread = resolvedBy(d);
        return thread !== null && state.canResolve(thread) ? [thread] : [];
      })
    : [];
  return {
    verdict: due ? state.verdict : null,
    body: due ? reviewBody(state.body, inline) : '',
    commit,
    inline,
    conversation,
    notIncluded: order.filter((d) => !isIncluded(d)),
    otherCommit: accepted.filter(elsewhere),
    resolve: [...new Set(resolve)],
  };
}

/** Between the parts of a review's body: the summary and each pull request comment. */
export const BODY_BREAK = '\n\n---\n\n';

/**
 * The review's body: the summary as typed, then the text of each pull
 * request comment (`pr` drafts) among `inline`, in list order, each
 * apart from the last by a rule. GitHub has no place for a comment on the
 * whole pull request inside a review but its body, so that is where they
 * go; the server publishes them with the review (and deletes them) without
 * adding them as threads.
 */
export function reviewBody(summary: string, inline: readonly ReviewDraft[]): string {
  const parts = [summary.trim() ? summary : '', ...inline.filter((d) => d.target.kind === 'pr').map((d) => d.body)].filter((p) => p.trim());
  return parts.join(BODY_BREAK);
}

/** How many comments a publish of `plan` sends. */
export const sentCount = (plan: PublishPlan): number => plan.inline.length + plan.conversation.length;
const comments = sentCount;

/** What becomes of a draft when `plan` is published. */
export type Fate =
  /** Sent in the review. */
  | 'review'
  /** Sent to the conversation (its line is outside the diff). */
  | 'conversation'
  /** Included, but on another commit than the review's: it waits for a later review. */
  | 'later'
  /** Not included: it stays a draft. */
  | 'notIncluded';

/** What becomes of draft `id` when `plan` is published. */
export function fateOf(plan: PublishPlan, id: ReviewDraft['id']): Fate {
  const has = (list: readonly ReviewDraft[]) => list.some((d) => d.id === id);
  if (has(plan.inline)) return 'review';
  if (has(plan.conversation)) return 'conversation';
  return has(plan.otherCommit) ? 'later' : 'notIncluded';
}

/**
 * Why a publish of `plan` with `verdict` would send nothing, in words for
 * the note beside the disabled button; null when it sends something (a
 * comment, a summary or a verdict). With drafts there, the way on is to
 * tick one.
 */
export function nothingToSend(plan: PublishPlan, verdict: Verdict): string | null {
  if (verdict !== 'COMMENT' || plan.body.trim() || sentCount(plan)) return null;
  const drafts = plan.notIncluded.length + plan.otherCommit.length;
  return drafts ? 'Nothing is ticked. Tick the comments to send, or add a summary or a verdict.' : 'Add a comment, a summary or a verdict to publish.';
}

/**
 * The draft the cursor goes to once `id` is ticked: the next one in the
 * list `order`, so ticking through a review is one key each; the last one
 * keeps the cursor.
 */
export function afterTick(order: readonly ReviewDraft[], id: ReviewDraft['id']): ReviewDraft['id'] {
  const at = order.findIndex((d) => d.id === id);
  return order[at + 1]?.id ?? id;
}
const countOf = (n: number) => `${n} comment${n === 1 ? '' : 's'}`;

/**
 * What the Review view says is published whatever the verdict: the comments
 * (and the summary, when there is one), or that there is nothing yet.
 */
export function publishLead(plan: PublishPlan): string {
  const n = comments(plan);
  const summary = plan.body.trim() !== '';
  if (n && summary) return `Publishing ${countOf(n)} and your summary`;
  if (n) return `Publishing ${countOf(n)}`;
  return summary ? 'Publishing your summary' : 'Nothing to publish yet';
}

/**
 * The publish button, naming what goes: the comments, then the verdict
 * chosen (`COMMENT` is no verdict): "Publish 2 comments + Approve",
 * "Approve" with no comments, "Publish 2 comments" with none.
 */
export function publishLabel(plan: PublishPlan, verdict: PublishState['verdict']): string {
  const n = comments(plan);
  const name = verdict === 'APPROVE' ? 'Approve' : verdict === 'REQUEST_CHANGES' ? 'Request changes' : null;
  if (name) return n ? `Publish ${countOf(n)} + ${name}` : name;
  if (n) return `Publish ${countOf(n)}`;
  return plan.body.trim() ? 'Publish summary' : 'Publish';
}

/**
 * The header's button to the Review view, from how many drafts there are
 * and how many are ticked: what a publish sends now ("Publish 2 of 5
 * drafts", "Publish 5 drafts"), or that none is ticked yet ("Publish: 0 of
 * 5 ticked"). Null with no drafts: the button then names no drafts.
 */
export function headerPublishLabel(included: number, total: number): string | null {
  if (!total) return null;
  const word = total === 1 ? 'draft' : 'drafts';
  if (!included) return `Publish: 0 of ${total} ticked`;
  return included >= total ? `Publish ${total} ${word}` : `Publish ${included} of ${total} ${word}`;
}

/** The request that publishes `plan`: nothing in it that the plan does not show. */
export function publishRequest(plan: PublishPlan): PublishRequest {
  const seen = (d: ReviewDraft) => ({ id: d.id, updated_at: d.updated_at });
  return {
    review: plan.verdict ? { event: plan.verdict, body: plan.body, commit_sha: plan.commit } : null,
    inline: plan.inline.map(seen),
    conversation: plan.conversation.map(seen),
    resolve: plan.resolve,
  };
}

/** A draft as the preview compares it: which one, as of when, in which state. */
const seen = (d: ReviewDraft) => `${d.id}:${d.updated_at}:${d.state.kind}`;

/**
 * The frozen preview `frozen` with the drafts `ids` as they are in `live`
 * now: changed, gone or (a discard undone) back. Those are the drafts the
 * viewer is changing on the page itself, which they have by definition
 * seen; every other draft stays as frozen, so a change made elsewhere
 * still shows as one (`previewState`). A draft the preview has never held
 * is listed as it is while it is not included (a reply just written
 * below, an agent's proposal arriving): it cannot be sent before the
 * viewer ticks it here, so it needs no second look. One that arrives
 * already included is left out, and shows as a change. The result is in
 * `live`'s order, then the frozen drafts `live` no longer has.
 */
export function adopt(frozen: readonly ReviewDraft[], live: readonly ReviewDraft[], ids: ReadonlySet<ReviewDraft['id']>): ReviewDraft[] {
  const was = new Map(frozen.map((d) => [d.id, d]));
  const now = new Set(live.map((d) => d.id));
  return [
    ...live.flatMap((d) => (ids.has(d.id) || (!was.has(d.id) && !isIncluded(d)) ? [d] : was.has(d.id) ? [was.get(d.id)!] : [])),
    ...frozen.filter((d) => !now.has(d.id) && !ids.has(d.id)),
  ];
}

/**
 * The publish preview, frozen as `frozen` when it was shown, against the
 * drafts now: `loading` until frozen, `current` while they are the same
 * drafts (same IDs, times and states), `changed` once any differs; a
 * changed preview is looked at again before publishing.
 */
export function previewState(frozen: readonly ReviewDraft[] | null, live: readonly ReviewDraft[]): 'loading' | 'current' | 'changed' {
  if (frozen === null) return 'loading';
  return frozen.map(seen).join('|') === live.map(seen).join('|') ? 'current' : 'changed';
}

/**
 * What a publish of `plan` does, in words ("3 published, 2 stay as
 * drafts"); given the server's `answer`, what it did ("2 published, 1 not
 * posted, …").
 */
export function planSummary(plan: PublishPlan, answer?: Pick<PublishResponse, 'published' | 'kept'>): string {
  const going = answer ? answer.published.length : plan.inline.length + plan.conversation.length;
  const parts = [going ? `${going} published` : plan.verdict ? 'Review published' : 'Nothing published'];
  if (answer?.kept.length) parts.push(`${answer.kept.length} not posted`);
  if (plan.notIncluded.length) parts.push(`${plan.notIncluded.length} ${plan.notIncluded.length === 1 ? 'stays a draft' : 'stay as drafts'}`);
  if (plan.otherCommit.length) parts.push(`${plan.otherCommit.length} kept for a later review`);
  return parts.join(', ');
}
